import React, { useEffect } from "react";
import { Badge, IconButton, Tooltip, useTheme } from "@mui/material";
import { NotificationsNoneOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetNotificationUnreadCountQuery } from "state/api";
import { setAppBadge } from "native/badge";

/**
 * Navbar bell — entry point to the in-app notification center (all roles;
 * for Financial Manager this is the ONLY affordance since FM routes aren't
 * in every bottom-nav variant).
 *
 * The unread count fetches once on app open and is invalidated by mark-read
 * mutations and by the service worker's "push-received" message
 * (pwa/useSwMessages.js). Deliberately NO pollingInterval/refetchOnFocus —
 * house rule is manual + event-driven only.
 */
export default function NotificationsBell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const { data } = useGetNotificationUnreadCountQuery(undefined, {
    skip: !token,
  });
  const count = data?.count ?? 0;

  // Keep the installed-app icon badge TRUTHFUL: it mirrors the unread count
  // while the app is open (the service worker sets a flag-badge for pushes
  // that arrive while it's closed; the store app's pushes carry the count),
  // and clears only when unread hits zero — not blindly on focus like before.
  // native/badge.js picks the browser Badging API or the store app's plugin.
  useEffect(() => {
    if (data === undefined) return;
    setAppBadge(count);
  }, [count, data]);

  return (
    <Tooltip title="Notifications">
      <IconButton
        aria-label={
          count > 0 ? `Notifications (${count} unread)` : "Notifications"
        }
        onClick={() => navigate("/notifications")}
      >
        <Badge
          badgeContent={count}
          max={99}
          color="error"
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.62rem",
              minWidth: 16,
              height: 16,
            },
          }}
        >
          <NotificationsNoneOutlined
            // Explicit ink: the theme never sets palette.mode:"dark", so MUI's
            // default action color would resolve for a light scheme.
            sx={{ color: theme.palette.extras.surface.textHigh }}
          />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
